import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { faFileExport, faFileImport, faSave } from '@fortawesome/free-solid-svg-icons';
import { FileUploadModule, FileUploader } from 'ng2-file-upload';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AdvancedGridviewComponent } from 'src/app/controls/advanced-gridview/advanced-gridview.component';
import { AdvancedDataSource } from 'src/app/controls/advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from 'src/app/controls/advanced-gridview/models/advanced-gridview-model';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { MenuItemEvent } from 'src/app/controls/advanced-gridview/models/menu-item-event';
import { RowClickEvent } from 'src/app/controls/advanced-gridview/models/row-click-event';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { FormMode } from 'src/app/controls/common/form-mode';
import { ConfirmDialogComponent } from 'src/app/controls/confirm-dialog/confirm-dialog.component';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { HttpError } from 'src/app/core/http-error';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { Rights } from 'src/app/models/enums/rights';
import { IIdentifiable } from 'src/app/models/interfaces/identifiable.interface';
import { ISortable } from 'src/app/models/interfaces/sortable.interface';
import { DefaultHttpService } from 'src/app/services/default-http.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { AdvancedGridviewComponent as AdvancedGridviewComponent_1 } from '../advanced-gridview/advanced-gridview.component';
import { AdvancedService } from '../advanced-gridview/services/advanced-service';
import { ICopyable } from '../common/icopyable';
import { DefaultFormContext } from '../forms/default-form-context';

@Component({
    selector: 'app-crud-form',
    templateUrl: './crud-form.component.html',
    styleUrls: ['./crud-form.component.scss'],
    imports: [AdvancedGridviewComponent_1, MatButtonModule, FileUploadModule, NgxSpinnerModule],
})
export class CrudFormComponent<T extends ICopyable<T>, U extends IIdentifiable, V> extends DefaultHttpService implements OnInit, AfterViewInit {
    /** Icônes utilisées dans le template. */
    public faFileExport = faFileExport;
    public faFileimport = faFileImport;
    public faSave = faSave;

    /** Pour être utilisable dans le template */
    FormMode: any = FormMode;

    /**
     * Reference au composant AdvancedGridview.
     */
    @ViewChild(AdvancedGridviewComponent)
    public gridView: AdvancedGridviewComponent<T>;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: DefaultFormContext<U> = null;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<DefaultFormContext<U>> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: DefaultFormContext<U>) {
        this._context$.next(value);
    }

    @Input()
    public sortEnabled: boolean = false;

    @Input()
    public className: string = '';

    /**
     * Mise en page de la GridView.
     */
    tableModel: AdvancedGridviewModel;

    /**
     * Source de données de la GridView.
     */
    dataSource: AdvancedDataSource<T>;

    /**
     * Retourne les Définitions des colonnes de la Gridview par défaut (sans colonne Id).
     */
    get defaultTableConfig(): ColumnDefinition[] {
        throw new Error('not implemented');
    }

    /**
     * Retourne les définitions des colonnes de la Gridview avec colonne Id.
     */
    get withIdTableConfig(): ColumnDefinition[] {
        throw new Error('not implemented');
    }

    /**
     * Accès au composant d'upload de fichier
     */
    @ViewChild('fileToUpload', { static: true })
    fileToUploadVariable: ElementRef;

    /**
     * Gestionnaire d'upload de fichiers.
     */
    public uploader: FileUploader = null;

    /**
     * Valeur indiquant si la liste est en lecture seule (absence de bouton et de menu contextuel)
     */
    @Input()
    public readOnly: boolean = false;

    /**
     * Emis lorsque l'enregistrement est mis à jour.
     */
    @Output()
    public recordUpdated: EventEmitter<T[]> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur valide l'ordre des élémenents (boite de dialogue Détermination de l\'ordre des éléments).
     */
    @Output()
    public itemsSorted: EventEmitter<ISortable[]> = new EventEmitter();

    /**
     * Emis lorsque l'enregistrement est mis à jour.
     */
    @Output()
    public recordRefreshed: EventEmitter<T[]> = new EventEmitter();

    /**
     * Elements du menu contextuel du composant AdvancedGridview.
     */
    public menuItems: ListItem<MenuITemAction>[] = [new ListItem(MenuITemAction.Read, 'Consulter')];

    /**
     * Constructeur.
     * @param enumHelper: Classe utilitaire pour les énumérations.
     * @param notificationService: Service utilisé pour gérer l'affichage des notifications.
     * @param spinner: Service utilisé pour gérer l'affichage du spinner.
     * @param dialog: Service pour ouvrir des boites de dialogue.
     * @param memoryService: Service pour récupérer les informations en mémoire.
     */
    constructor(
        public enumHelper: EnumHelper,
        protected notificationService: NotificationService,
        protected spinner: NgxSpinnerService,
        public dialog: MatDialog,
        public override memoryService: MemoryService
    ) {
        super(memoryService);

        this.AddMenuItem('Dupliquer', MenuITemAction.Duplicate, this.getCreationRight());
        this.AddMenuItem('Éditer', MenuITemAction.Update, this.getUpdateRight());
        this.AddMenuItem('Supprimer', MenuITemAction.Delete, this.getDeletionRight());
    }

    /**
     * Initialise le composant.
     */
    ngOnInit() {
        console.log('CrudFormComponent --> ngOnInit');
        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            console.log('CrudFormComponent --> context change', x);
            let newContext = x;
            if (newContext === null || newContext === undefined) {
                // do nothing
            } else if (this._context === null || this._context.item.id !== newContext.item.id) {
                this._context = newContext;

                if (this._context.item) {
                    let datasourceContext = this.getDatasourceContext();
                    this.dataSource = new AdvancedDataSource(datasourceContext, this.getDatasourceService(), this.spinner, this.notificationService);
                    console.log('CrudFormComponent --> instantiation AdvancedGridviewModel');

                    if (this.memoryService.getConnectedUser().params.idVisible) {
                        this.tableModel = new AdvancedGridviewModel(this.withIdTableConfig, this.getDefaultSortInfos(), this.isActionColumnVisible());
                    } else {
                        this.tableModel = new AdvancedGridviewModel(this.defaultTableConfig, this.getDefaultSortInfos(), this.isActionColumnVisible());
                    }

                    var columnsDefinitions = this.tableModel.displayedColumnsDefinitions.filter((item) => this.enumHelper.areEquals(ColumnType, item.type, ColumnType.List));
                    this.loadListColumnDatas(datasourceContext, columnsDefinitions as ColumnListDefinition<string>[]);
                }

                if (!!this.gridView) {
                    this.gridView.refresh();
                }
            } else {
                this._context = newContext;
                this.dataSource.reloadRecords();
            }
        });

        this.dataSource.datasSubject.subscribe((x) => this.onRecordRefreshed(x));

        if (this.readOnly === false) {
            this.uploader = new FileUploader({
                url: `${this.baseUrl}/api/organizations/${this.organizationId}/customers/${this._context.item.id}/articlealiases/import`,
                authTokenHeader: 'Authorization',
                authToken: `Bearer ${this.connectedUser.token.accessToken}`,
                allowedFileType: ['xls'],
                queueLimit: 1,
            });

            this.uploader.onAfterAddingFile = (fileItem) => {
                fileItem.withCredentials = false;
            };

            this.uploader.onCompleteAll = () => {
                this.gridView.refresh();
            };
        }
    }

    ngAfterViewInit(): void {
        this.gridView.getSortableItemsFunction = this.getSortablesItems.bind(this);
    }

    /**
     * Raffraichit le contenu de la liste.
     */
    public refresh() {
        this.gridView.refresh();
    }

    /**
     * Appellé lorsqu'un fichier à uploader est selectionné.
     */
    public onFileSelected() {
        this.uploader.uploadAll();
    }

    /**
     * Spécifie les éléments d'un filtre de colonne de type liste.
     * @param column : Instance de la colonne de type liste
     * @param listItem : Liste des éléments du filtre.
     */
    protected setColumnFilterDatas(column: ColumnListDefinition<string>, listItem: ListItem<string>[]): void {
        if (!!column) {
            this.gridView.SetFilterItems(column, listItem);
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton Ajouter.
     */
    public onCreate(): void {
        console.log('Ajouter');
        let record = this.instantiateNewRecord();
        this.displayFormDialog(record, FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur double-clic sur un enregistrement.
     */
    public onRowDoubleClick(event: RowClickEvent<T>) {
        console.log('consulter');
        let record = event.context;
        this.displayFormDialog(record, FormMode.Reading);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton Exporter.
     */
    public onRecordsExported() {
        this.dataSource.export(this.getExportFileName());
    }

    /**
     * Retourne une valeur indiquant si la colonne Action est visible.
     * Remarque : Par défaut retourne true. A surcharger pour modifier le comportement.
     * @returns True, si la colonne Action est visible.
     */
    public isActionColumnVisible() {
        return true;
    }

    /**
     * Ajoute un élément au menu contextuel.
     * @param menuItemLabel : Libellé de l'élément.
     * @param menuItemAction : Action lié à l'élément.
     * @param neededRight : Droit necessaire pour avoir acces à cet élément.
     */
    protected AddMenuItem(menuItemLabel: string, menuItemAction: MenuITemAction, neededRight: Rights) {
        if (this.memoryService.getConnectedUser().hasRight(neededRight)) {
            this.menuItems.push(new ListItem(menuItemAction, menuItemLabel));
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement retourné par le menu contextuel.
     */
    public onAction(event: MenuItemEvent<T>): void {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            let record = event.context;
            this.displayFormDialog(record, FormMode.Reading);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            let record = event.context.createCopy();
            this.displayFormDialog(record, FormMode.Edition);
        } else if (event.action == MenuITemAction.Duplicate) {
            // Cas de la duplication.
            let record = this.duplicate(event.context);
            this.displayFormDialog(record, FormMode.Creation);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
            this.displayDeleteConfirmationDialog(this.getDeleteConfirmationText(event.context), event.context);
        }
    }

    /**
     * Affiche la boite de dialogue de confirmation de suppression.
     * @param confirmationText : Texte de confirmation.
     * @param item : Instance de l'élément à supprimer.
     */
    protected displayDeleteConfirmationDialog(confirmationText: string, item: T) {
        // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Confirmation de suppression',
                message: confirmationText,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de confirmation
        dialogRef.afterClosed().subscribe((dialogResult: T) => {
            if (dialogResult) {
                // Suppression effective du client.
                this.spinner.show('main-spinner');
                this.getDeletionRequest(item).subscribe(
                    (x) => {
                        this.notificationService.displaySuccess(null, this.getDeletionText());
                        this.refresh();
                        this.spinner.hide('main-spinner');
                    },
                    (error: HttpError) => {
                        this.notificationService.displayError('Erreur', this.getDeletionErrorText() + error.message);
                        this.spinner.hide('main-spinner');
                    }
                );
            }
        });
    }

    /**
     * Affiche la boite de dialogue contenant le formulaire de création / édition / consultation.
     */
    protected displayFormDialog(record: T, mode: FormMode) {
        const dialogRef = this.dialog.open(this.getDialogComponent(), {
            maxWidth: '400px',
            data: {
                mode: mode,
                record: record,
                context: this.context,
                dataSource: this.dataSource,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue.
        dialogRef.afterClosed().subscribe((dialogResult: T) => {
            if (dialogResult) {
                if (mode === FormMode.Creation) {
                    // Cas de la création ou de la duplication.
                    this.spinner.show('main-spinner');
                    this.getCreationRequest(dialogResult).subscribe(
                        (x) => {
                            this.refresh();
                            this.onRecordUpdated(this.dataSource.datas);
                            this.notificationService.displaySuccess(null, this.getCreationText());
                            this.spinner.hide('main-spinner');
                        },
                        (error: HttpError) => {
                            this.notificationService.displayError('Erreur', this.getCreationErrorText() + error.message);
                            this.spinner.hide('main-spinner');
                        }
                    );
                } else if (mode === FormMode.Edition) {
                    // Cas de l'édition.
                    this.spinner.show('main-spinner');
                    this.getUpdateRequest(dialogResult).subscribe(
                        (x) => {
                            this.refresh();
                            this.onRecordUpdated(this.dataSource.datas);
                            this.notificationService.displaySuccess(null, this.getUpdateText());
                            this.spinner.hide('main-spinner');
                        },
                        (error: HttpError) => {
                            this.notificationService.displayError('Erreur', this.getUpdateErrorText() + error.message);
                            this.spinner.hide('main-spinner');
                        }
                    );
                }
            }
        });
    }

    /**
     * Charge les éléments des filtres d'une colonnes de type liste.
     * A surcharger.
     * @param datasourceContext
     * @param columns
     */
    protected loadListColumnDatas(datasourceContext: Context, columns: ColumnListDefinition<string>[]): void {
        throw new Error('not implemented');
    }

    /**
     * Instancie un nouvel enregistrement.
     * A surcharger.
     */
    protected instantiateNewRecord(): T {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte de confirmation de la suppression.
     * A surcharger.
     */
    protected getDeleteConfirmationText(item: T): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte de suppression.
     * A surcharger.
     */
    protected getDeletionText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte d'erreur de suppression.
     * A surcharger.
     */
    protected getDeletionErrorText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte de création.
     * A surcharger.
     */
    protected getCreationText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte d'erreur de création.
     * A surcharger.
     */
    protected getCreationErrorText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte de mise à jour.
     * A surcharger.
     */
    protected getUpdateText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte d'erreur de mise à jour.
     * A surcharger.
     */
    protected getUpdateErrorText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte de changement d'ordre.
     * A surcharger.
     */
    protected getApplyOrderChangesText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le texte d'erreur de changement d'ordre.
     * A surcharger.
     */
    protected getApplyOrderChangesErrorText(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne la requête pour supprimer un enregistrement.
     * A surcharger.
     * @param item : Instance de l'enregistrement à supprimer.
     */
    protected getDeletionRequest(item: T): Observable<boolean> {
        throw new Error('not implemented');
    }

    /**
     * Retourne la requête pour créer un enregistrement.
     * A surcharger.
     * @param item : Instance de l'enregistrement à créer.
     */
    protected getCreationRequest(item: T): Observable<T> {
        throw new Error('not implemented');
    }

    /**
     * Retourne la requête pour mettre à jour un enregistrement.
     * A surcharger.
     * @param item : Instance de l'enregistrement à mettre à jour.
     */
    protected getUpdateRequest(item: T): Observable<T> {
        throw new Error('not implemented');
    }

    /**
     * Retourne la requête pour uploader le fichier d'import.
     * A surcharger.
     */
    protected getUploadRequest(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne la requête pour mettre à jour l'ordre des enregistrements.
     * A surcharger.
     * @param items : Liste des instances d'enregistrements dont l'ordre doit être modifié.
     */
    protected getApplyOrderChangesRequest(items: ISortable[]): Observable<boolean> {
        throw new Error('not implemented');
    }

    /**
     * Retourne le composant boite de dialogue utilisé pour afficher le formulaire de création / édition / consultation.
     * A surcharger.
     */
    protected getDialogComponent(): new (...args: any[]) => V {
        throw new Error('not implemented');
    }

    /**
     * Retourne le nom du fichier d'export.
     * A surcharger.
     */
    protected getExportFileName(): string {
        throw new Error('not implemented');
    }

    /**
     * Retourne le duplicata d'un enregistrement.
     * A surcharger.
     * @param item : Instance de l'enregistrement à dupliquer.
     */
    protected duplicate(item: T): T {
        throw new Error('not implemented');
    }

    /**
     * Retourne la source de données de la liste.
     * A surcharger.
     */
    protected getDatasourceService(): AdvancedService<T> {
        throw new Error('not implemented');
    }

    /**
     * Retourne le contexte utilisé par la source de données de la liste.
     * A surcharger.
     */
    protected getDatasourceContext(): Context {
        throw new Error('not implemented');
    }

    /**
     * Retourne les infos de tri par défaut de la liste.
     * A surcharger.
     */
    protected getDefaultSortInfos(): SortInfos {
        throw new Error('not implemented');
    }

    /**
     * Retourne le droit necessaire pour pouvoir crééer un enregistrement.
     * A surcharger.
     */
    public getCreationRight(): Rights {
        throw new Error('not implemented');
    }

    /**
     * Retourne le droit necessaire pour pouvoir mettre à jour un enregistrement.
     * A surcharger.
     */
    protected getUpdateRight(): Rights {
        throw new Error('not implemented');
    }

    /**
     * Retourne le droit necessaire pour pouvoir supprimer un enregistrement.
     * A surcharger.
     */
    protected getDeletionRight(): Rights {
        throw new Error('not implemented');
    }

    protected getSortablesItems(item: T): Observable<T[]> {
        return of(
            this.dataSource.datas.sort((a, b) => {
                return (a as unknown as ISortable).order < (b as unknown as ISortable).order ? -1 : 1;
            })
        );
    }

    public onItemsSorted(items: ISortable[]) {
        this.spinner.show('main-spinner');

        this.getApplyOrderChangesRequest(items).subscribe(
            (x) => {
                this.refresh();
                this.onRecordUpdated(this.dataSource.datas);
                this.notificationService.displaySuccess(null, this.getApplyOrderChangesText());
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', this.getApplyOrderChangesErrorText() + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    public onRecordUpdated(data: T[]) {
        this.recordUpdated.emit(data);
    }

    public onRecordRefreshed(data: T[]) {
        this.recordRefreshed.emit(data);
    }
}
